import { aircraftDetailQuery } from "@common/api/aircraft"
import { atlPageQuery } from "@common/api/atl"
import { WO_PAGES } from "@common/flags"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type { GroupOrder } from "@/components/layout/use-group-paging"
import { requireFeatureFlag } from "@/lib/require-feature-flag"
import {
  useGroupedTechLogPages,
  useTechLogPages,
} from "../-components/tech-log/use-tech-log-pages"
import { type AtlPage, techLogDisplayOf } from "../-lib/tech-log"
import { PageForm } from "./-components/form/page-form"
import { PagePane } from "./-components/page-pane"
import { CLOSED_SCAN } from "./-components/source"

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/tech-log/$pageId",
)({
  beforeLoad: ({ context: { queryClient } }) =>
    requireFeatureFlag(queryClient, WO_PAGES),
  loader: ({ context: { queryClient }, params: { pageId } }) =>
    queryClient.ensureQueryData(atlPageQuery({ atl_page_id: pageId })),
  component: TechLogPagePane,
})

function TechLogPagePane(): React.ReactElement {
  const search = Route.useSearch()
  return techLogDisplayOf(search).group ? (
    <GroupedPagePane />
  ) : (
    <ListedPagePane />
  )
}

type PageOrder = GroupOrder<AtlPage> & { total: number }

function ListedPagePane(): React.ReactElement {
  const { pages, count, paging } = useTechLogPages()
  const indexOf = (id: string) => pages.findIndex((page) => page.id === id)
  const order: PageOrder = {
    total: count,
    indexOf,
    previous: (id) => {
      const index = indexOf(id)
      return index > 0 ? pages[index - 1] : undefined
    },
    next: async (id) => {
      const index = indexOf(id)
      if (index === -1) return undefined
      const loaded = pages[index + 1]
      if (loaded || !paging.hasNextPage) return loaded
      return (await paging.fetchNextPage())[index + 1]
    },
  }
  return <SteppedPagePane order={order} />
}

function GroupedPagePane(): React.ReactElement {
  const { data, order } = useGroupedTechLogPages()
  return <SteppedPagePane order={{ ...order, total: data.count }} />
}

function SteppedPagePane({ order }: { order: PageOrder }): React.ReactElement {
  const { aircraftId, pageId } = Route.useParams()
  const { data: aircraft } = useSuspenseQuery(
    aircraftDetailQuery({ id: aircraftId }),
  )
  const { data: page } = useSuspenseQuery(atlPageQuery({ atl_page_id: pageId }))
  const canVoid = useHasPermission("atl_page:delete")
  const navigate = useNavigate({ from: Route.fullPath })
  const index = order.indexOf(pageId)
  const params = { aircraftId }
  const close = () =>
    navigate({
      to: "/aircraft/$aircraftId",
      params,
      search: (previous) => ({
        ...previous,
        tab: "tech-log" as const,
        ...CLOSED_SCAN,
        wide: undefined,
      }),
    })
  const open = (target: AtlPage | undefined) => {
    if (target) {
      void navigate({
        to: "/aircraft/$aircraftId/tech-log/$pageId",
        params: { ...params, pageId: target.id },
        search: (previous) => ({
          ...previous,
          ...CLOSED_SCAN,
        }),
      })
    }
  }
  const voidPage = canVoid
    ? () =>
        navigate({
          to: "/aircraft/$aircraftId/tech-log/void/$pageId",
          params: { ...params, pageId },
          search: true,
        })
    : undefined

  return (
    <PagePane
      title={page.log_number ?? "Draft"}
      page={page}
      stepper={
        index === -1
          ? undefined
          : {
              index,
              total: order.total,
              onPrevious: index > 0 ? () => open(order.previous(pageId)) : null,
              onNext:
                index < order.total - 1
                  ? () => void order.next(pageId).then(open)
                  : null,
            }
      }
      onClose={close}
    >
      <PageForm
        key={page.id}
        subject={{ kind: "recorded", aircraft, page }}
        onVoid={voidPage}
      />
    </PagePane>
  )
}
